import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { HeaderTokenService } from '@msi/commandcentral-common-header';
import { TreeNodeSelectEvent } from 'primeng/tree';
import { Subscription, combineLatest } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { AdminRestService } from '@rest/admin-rest.service';
import { ThemeSwitcherService } from '@services/theme-switcher.service';
import { UserService } from '@services/user.service';
import { ToastServiceManager } from '@services/toast-service-manager.service';
import { TemplateConstants } from './constants/template-constants';
import { CurrentUnitsService } from '@services/current-units.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private selectedCustomerId?: string;
  public THEME_TEXTS = TemplateConstants.ThemeTexts;
  public lightMode: boolean = false;
  public ThemeText: string = this.THEME_TEXTS[this.lightMode ? 0 : 1];
  public appReady: boolean = false;
  public startupError: boolean = false;
  private readonly defaultLanguage: string = 'en';
  public ComponentLoading: boolean = true;
  public ShowPremiseHazards: boolean = true;
  public ShowConfiguration: boolean = true;

  private agencySwitchingSubscription!: Subscription;
  private navNodesSubscription?: Subscription;
  private routerSubscription?: Subscription;
  private paramsCustomerId!: string;
  private paramsAgencyId!: string;

  public activeIndex: number = 0;

  public PremiseHazardTabText: string = 'Hazards';
  public PremiseHazardTypeTabText: string = 'Hazard Types';
  public OrganizationTabText: string = 'Organization Data Share';

  private routes: string[] = ['/hazards', '/types', '/organization-configuration'];
  private unauthorizedRoute: string = '/unauthorized';
  private initialSyncCompleted: boolean = false;
  private navigationInProgress: boolean = false;

  constructor(
    private toastServiceManager: ToastServiceManager,
    private route: ActivatedRoute,
    private themeSwitcherService: ThemeSwitcherService,
    private userService: UserService,
    private adminRest: AdminRestService,
    private router: Router,
    private transloco: TranslocoService,
    private headerTokenService: HeaderTokenService,
    private cdr: ChangeDetectorRef,
    private currentUnitsService: CurrentUnitsService,
  ) { 

  }

  ngOnInit(): void {
    this.readRouterParams();
    this.routerSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => this.syncSelectedNavNode(event.urlAfterRedirects));
  }

  private buildNavNodes(): void {
    this.transloco.selectTranslate(['Sidenav.hazards', 'Sidenav.types', 'Sidenav.settings']).subscribe((result) => {
      this.PremiseHazardTabText = result[0];
      this.PremiseHazardTypeTabText = result[1];
      this.OrganizationTabText = result[2];
    });
  }

  private syncSelectedNavNode(url: string): void {
    if (url.localeCompare(this.unauthorizedRoute, undefined, { caseFirst: 'upper' }) === 0 
        || !this.initialSyncCompleted 
        || this.navigationInProgress) {
      return;
    }

    this.ComponentLoading = true;

    const pageIdx = this.activeIndex;

    if (pageIdx < 0 || pageIdx >= this.routes.length) {
      this.updateActiveIndex(0);
      return;
    }

    const currentRoute = this.router.url.split('?')[0];
    let effectiveRoute = currentRoute;
    const routeExists = this.routes.includes(effectiveRoute);
    if (currentRoute === '/' || !routeExists) {
      effectiveRoute = this.routes[0];
    }

    if (effectiveRoute !== this.routes[pageIdx]) {
      this.navigationInProgress = true;
      this.router.navigateByUrl(this.routes[pageIdx]).then(() => {
        this.ComponentLoading = false;
        this.navigationInProgress = false;
      });
    } else {
        this.ComponentLoading = false;
        this.navigationInProgress = false;
    }
  }

  private onError(errorMessage: string): void {
    this.toastServiceManager.criticalError(errorMessage, undefined, { closeButton: true });
    this.startupError = true;
    this.appReady = true;
  }

  private readRouterParams(): void {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd), take(1))
      .subscribe((event: NavigationEnd): void => {
        this.handleRouteParams();
      });
  }

  private handleRouteParams(): void {
    const params = this.route.snapshot.firstChild?.queryParamMap;

    if (params?.has('theme')) {
      const theme = params!.get('theme')!;
      const isLightMode = theme.toLowerCase() === 'light';
      this.themeSwitcherService.setTheme(isLightMode);
      this.lightMode = isLightMode;
    }
    else {
      this.themeSwitcherService.setTheme(false);
      this.lightMode = false;
    }

    if (params?.has('customerId')) {
      this.paramsCustomerId = params!.get('customerId')!;
    }
    if (params?.has('agencyId')) {
      this.paramsAgencyId = params!.get('agencyId')!;
    }
    if (params?.has('selectedCustomerId')) {
      this.selectedCustomerId = params!.get('selectedCustomerId')!;
    }
    let language: string | undefined = undefined;
    if (params?.has('language')) {
      language = params!.get('language')!;
    }
    this.transloco.setActiveLang(language ?? this.defaultLanguage);
    this.loadTranslations();
  }

  private authorize(): void {
    this.headerTokenService.tokenReady.pipe(take(1)).subscribe(isTokenReady => {
        if (isTokenReady) {
          this.headerTokenService.getActiveAgencyId();
          this.headerTokenService.activeAgencyId.pipe(take(1)).subscribe(agencyId => {
            this.headerTokenService.getToken().then(token => {
              const isSuperAdmin: boolean = agencyId.localeCompare('motorola-solutions', undefined, { sensitivity: 'case'}) === 0;
              if (isSuperAdmin) {
                this.userService.setUserInformation({
                  agencyId: agencyId,
                  customerId: agencyId,
                  isSuperAdmin: true,
                  token: token,
                  selectedCustomerId: this.selectedCustomerId ?? this.paramsCustomerId ?? this.paramsAgencyId ?? agencyId,
                });
              } else {
                this.userService.setUserInformation({
                  agencyId: agencyId,
                  customerId: agencyId,
                  isSuperAdmin: false,
                  token: token,
                  selectedCustomerId: this.selectedCustomerId,
                });
              }
              this.adminRest.initializeCustomer().pipe(take(1)).subscribe(() => {
                this.adminRest.getDefaultUnits().pipe(take(1)).subscribe((result) => {
                  this.adminRest.getUserPermissions().pipe(take(1)).subscribe((permissionResults => {
                    this.syncInitialNavigationState();
                    this.userService.setUserPermissions(permissionResults);
                    this.ShowPremiseHazards = this.userService.hasQueryPermissions();
                    this.ShowConfiguration = this.userService.hasAdminPermissions();
                    this.buildNavNodes();
                    this.appReady = true;
                  }));
                });
              });
            });
          }
        );
      }
    });
  }

  ngOnDestroy(): void {
    this.agencySwitchingSubscription?.unsubscribe();
    this.navNodesSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
  }

  private loadTranslations(): void {    
      this.authorize();
  }

  public onThemeToggle(event: any): void {
    this.lightMode = !this.lightMode;
    if (this.lightMode) {
      this.ThemeText = this.THEME_TEXTS[0];
    } else {
      this.ThemeText = this.THEME_TEXTS[1];
    }
    this.themeSwitcherService.setTheme(this.lightMode);
  }
  public updateActiveIndex($event: any) : void {
    this.activeIndex = parseInt($event.toString());
    this.syncSelectedNavNode(this.router.url);
  }

  private syncInitialNavigationState(): void {
    this.initialSyncCompleted = true;
    const url = this.router.url.split('?')[0];
    const pageIdx = this.activeIndex;
    let effectiveUrl = url;
    if (url === '/') {
      effectiveUrl = this.routes[0];
    }
    let unauthorized: boolean = effectiveUrl === '/unauthorized';
    if (unauthorized) {
      return;
    }
    const routeExists = this.routes.includes(effectiveUrl);
    if (!routeExists) {
      this.syncSelectedNavNode(this.routes[0]);
      return;
    }
    const routeIdx = this.routes.indexOf(effectiveUrl);
    if (routeIdx !== 0) {
      this.activeIndex = routeIdx;
    }
    this.syncSelectedNavNode(effectiveUrl);
  }
}
